import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  LinkIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  TrashIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

interface URLInfo {
  short_code: string;
  destination_url: string;
  short_url: string;
  created_at: string;
  access_count: number;
  is_active: boolean;
  custom_code: boolean;
  encoding_info: {
    prefix: string;
    middle_bytes: number;
    beacon_encoded: boolean;
  };
}

interface UsageStats {
  current_month: number;
  limit: number;
  reset_date: string;
  urls_created: number;
  total_clicks: number;
}

const URLs: React.FC = () => {
  const { getIdToken } = useAuth();
  const [urls, setUrls] = useState<URLInfo[]>([]);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [destinationUrl, setDestinationUrl] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [urlToDelete, setUrlToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUrls();
    fetchUsageStats();
  }, []);

  const fetchUrls = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/urls`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure data is an array
        setUrls(Array.isArray(data) ? data : []);
      } else {
        setError('Failed to fetch URLs');
        setUrls([]);
      }
    } catch (error) {
      setError('Failed to fetch URLs');
      setUrls([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/usage`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsageStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch usage stats:', error);
    }
  };

  const handleCreateUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');

    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/shorten`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          destination_url: destinationUrl,
          custom_code: customCode || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('URL creation response:', data);
        setSuccess(`URL created successfully! Short URL: ${data.short_url}`);
        setDestinationUrl('');
        setCustomCode('');
        setShowForm(false);
        // Wait a moment before fetching to ensure database consistency
        setTimeout(() => {
          fetchUrls();
          fetchUsageStats();
        }, 1000);
      } else {
        console.error('URL creation failed:', data);
        setError(data.message || data.detail || 'Failed to create URL');
      }
    } catch (error) {
      console.error('URL creation error:', error);
      setError('Failed to create URL');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyUrl = async (shortUrl: string) => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      setSuccess('URL copied to clipboard!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to copy URL');
    }
  };

  const handleDeleteUrl = (shortCode: string) => {
    setUrlToDelete(shortCode);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!urlToDelete) return;

    setDeleting(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/urls/${urlToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccess('URL deleted successfully!');
        fetchUrls();
        fetchUsageStats();
        setShowDeleteModal(false);
        setUrlToDelete(null);
      } else {
        setError('Failed to delete URL');
      }
    } catch (error) {
      setError('Failed to delete URL');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUrlToDelete(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUsagePercentage = () => {
    if (!usageStats || usageStats.limit === -1) return 0;
    return (usageStats.current_month / usageStats.limit) * 100;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">URL Management</h1>
            <p className="mt-2 text-sm text-gray-700">
              Create and manage your short URLs
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-macos text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors shadow-sm"
          >
            <LinkIcon className="h-4 w-4 mr-2" />
            Create URL
          </button>
        </div>
      </div>

      {/* Usage Stats */}
      {usageStats && (
        <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Usage This Month</h3>
            <span className="text-sm text-gray-500">
              Resets on {new Date(usageStats.reset_date).toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">URLs Created</span>
            <span className="text-sm font-medium text-gray-900">
              {usageStats.current_month} / {usageStats.limit === -1 ? '∞' : usageStats.limit}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-primary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(getUsagePercentage(), 100)}%` }}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Total URLs: </span>
              <span className="font-medium text-gray-900">{usageStats.urls_created}</span>
            </div>
            <div>
              <span className="text-gray-600">Total Clicks: </span>
              <span className="font-medium text-gray-900">{usageStats.total_clicks}</span>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-macos text-sm mb-6">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-macos text-sm mb-6">
          {success}
        </div>
      )}

      {/* Create URL Form */}
      {showForm && (
        <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Short URL</h3>
          <form onSubmit={handleCreateUrl} className="space-y-4">
            <div>
              <label htmlFor="destination-url" className="block text-sm font-medium text-gray-700 mb-2">
                Destination URL
              </label>
              <input
                id="destination-url"
                type="url"
                required
                className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-white placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                style={{ backgroundColor: 'white', color: '#111827' }}
                placeholder="https://example.com/your-long-url"
                value={destinationUrl}
                onChange={(e) => setDestinationUrl(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="custom-code" className="block text-sm font-medium text-gray-700 mb-2">
                Custom Code (Optional)
              </label>
              <input
                id="custom-code"
                type="text"
                className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-white placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                style={{ backgroundColor: 'white', color: '#111827' }}
                placeholder="my-custom-code"
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value)}
              />
              <p className="mt-1 text-sm text-gray-500">
                Leave empty for auto-generated code. Only alphanumeric characters allowed.
              </p>
            </div>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 py-3 px-4 border border-gray-300 text-sm font-medium rounded-macos text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="flex-1 py-3 px-4 border border-transparent text-sm font-medium rounded-macos text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {creating ? 'Creating...' : 'Create URL'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* URLs List */}
      <div className="bg-white rounded-macos-lg shadow-macos border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Your URLs</h3>
        </div>

        {!Array.isArray(urls) || urls.length === 0 ? (
          <div className="p-6">
            <div className="text-center py-12">
              <LinkIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No URLs yet</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating your first short URL.</p>
              <div className="mt-6">
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-macos text-white bg-primary-500 hover:bg-primary-600 transition-colors"
                >
                  Create URL
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {Array.isArray(urls) && urls.map((url) => (
              <div key={url.short_code} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Short URL with copy button */}
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="flex items-center space-x-2 bg-gray-100 rounded-macos px-3 py-2 min-w-0 flex-1">
                        <LinkIcon className="h-4 w-4 text-primary-500 flex-shrink-0" />
                        <span className="text-sm font-mono font-medium text-gray-900 truncate">
                          {url.short_url}
                        </span>
                        <button
                          onClick={() => handleCopyUrl(url.short_url)}
                          className="p-1 text-gray-400 hover:text-primary-600 transition-colors flex-shrink-0"
                          title="Copy URL"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Destination URL */}
                    <div className="flex items-start space-x-2 mb-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600 break-all">
                          {url.destination_url}
                        </p>
                      </div>
                    </div>

                    {/* Tags and Status */}
                    <div className="flex items-center space-x-2 mb-3">
                      {url.custom_code && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-macos text-xs font-medium bg-blue-100 text-blue-800">
                          Custom Code
                        </span>
                      )}
                      {url.encoding_info.beacon_encoded && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-macos text-xs font-medium bg-green-100 text-green-800">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5"></div>
                          Beacon Ready
                        </span>
                      )}
                      {!url.is_active && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-macos text-xs font-medium bg-red-100 text-red-800">
                          <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5"></div>
                          Inactive
                        </span>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-6 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded-full bg-gray-200 flex items-center justify-center">
                          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                        </div>
                        <span>Created {formatDate(url.created_at)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <EyeIcon className="h-3 w-3 text-gray-400" />
                        <span className="font-medium">{url.access_count}</span>
                        <span>clicks</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded bg-gray-200 flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-sm"></div>
                        </div>
                        <span>{url.encoding_info.middle_bytes} bytes</span>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center space-x-1 ml-4">
                    <button
                      className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-macos transition-all"
                      title="View Analytics"
                    >
                      <ChartBarIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUrl(url.short_code)}
                      className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-macos transition-all"
                      title="Delete URL"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete URL"
        message="Are you sure you want to delete this URL? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={deleting}
      />
    </div>
  );
};

export default URLs;